import { useState, useEffect } from 'react'

import { getPublishedWorkshopContents } from '@/lib/db'
import type { WorkshopContent } from '@/types'
import {
  Presentation,
  Loader2,
  Layers,
  Clock,
  PlayCircle
} from 'lucide-react'

export default function WorkshopMaterials() {
  const [contents, setContents] = useState<WorkshopContent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      setLoading(true)
      const { data } = await getPublishedWorkshopContents()
      if (data) setContents(data)
      setLoading(false)
    }
    load()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Presentation className="w-7 h-7 text-purple-500" />
          Material de talleres
        </h1>
        <p className="text-gray-400 mt-1">
          Material interactivo para proyectar durante los talleres en vivo
        </p>
      </div>

      {contents.length === 0 ? (
        <div className="text-center py-16 bg-gray-800 rounded-xl border border-gray-700">
          <Presentation className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-300">Sin material disponible</p>
          <p className="text-gray-500 mt-1">
            El administrador aún no ha publicado material para talleres.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contents.map(content => {
            const rawUnit = (content as any).unit
            const unit = Array.isArray(rawUnit) ? rawUnit[0] : rawUnit
            const unitName = unit?.name_es || unit?.title || 'Sin unidad'
            // Extract unit number from unit data, or parse from title as fallback
            const unitNumber = unit?.unit_number
              || content.title?.match(/[Uu]nidad\s*(\d+)/)?.[1]
              || content.title?.match(/[Uu]nit[ée]?\s*(\d+)/)?.[1]
              || '1'
            const blockCount = (content as any).blocks?.length || 0

            return (
              <div
                key={content.id}
                className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden hover:border-purple-600 transition-colors"
              >
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 bg-purple-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-bold text-lg">{unitNumber}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{content.title}</h3>
                      <p className="text-sm text-gray-400">{unitName}</p>
                    </div>
                  </div>

                  {content.description && (
                    <p className="text-sm text-gray-400 mb-4">{content.description}</p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {content.estimated_duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" />
                        {blockCount} bloques
                      </span>
                    </div>
                    <button
                      onClick={() => window.open(`/teacher/workshop-material/${content.id}/present`, '_blank', 'noopener')}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      <PlayCircle className="w-4 h-4" />
                      Presentar
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
